import React, { useState, useEffect, useCallback } from 'react';
import { contactsAPI, clientsAPI } from '../utils/api';
import { Plus, Search, Mail, Phone, Trash2 } from 'lucide-react';
import ContactModal from '../components/modals/ContactModal';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterClient) params.client_id = filterClient;
      const [contactsRes, clientsRes] = await Promise.all([
        contactsAPI.getAll(params),
        clientsAPI.getAll()
      ]);
      setContacts(contactsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [filterClient]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setShowCreateModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await contactsAPI.delete(deleteTarget._id);
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getClientName = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    return client?.client_name || 'Unknown';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Contacts</h1>
          <p className="text-slate-500 mt-1 text-sm">Manage your contact database</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 self-start sm:self-auto"
        >
          <Plus size={20} />
          New Contact
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <select
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Clients</option>
            {clients.map(c => (
              <option key={c._id} value={c._id}>{c.client_name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Name</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Client</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Designation</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Email</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Phone</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredContacts.map((contact) => (
              <tr key={contact._id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                      {contact.full_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{contact.full_name}</p>
                      {contact.decision_maker && (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">Decision Maker</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-slate-900">{getClientName(contact.client_id)}</td>
                <td className="py-3 px-4 text-sm text-slate-600">{contact.designation || 'N/A'}</td>
                <td className="py-3 px-4 text-sm text-slate-600">
                  {contact.email ? (
                    <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-primary hover:underline">
                      <Mail size={14} />
                      {contact.email}
                    </a>
                  ) : 'N/A'}
                </td>
                <td className="py-3 px-4 text-sm text-slate-600">
                  {contact.phone || 'N/A'}
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(contact)}
                      className="text-sm text-primary hover:text-primary/80"
                      data-testid={`edit-contact-${contact._id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => { setDeleteTarget(contact); setDeleteError(null); }}
                      className="text-red-400 hover:text-red-600 p-1"
                      data-testid={`delete-contact-${contact._id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredContacts.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <p>No contacts found</p>
          </div>
        )}
      </div>

      {showCreateModal && (
        <ContactModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedContact(null);
          }}
          contact={selectedContact}
          onSuccess={() => {
            fetchData();
            setShowCreateModal(false);
            setSelectedContact(null);
          }}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDeleteTarget(null)} data-testid="delete-contact-dialog">
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete Contact</h3>
            <p className="text-sm text-slate-600 mb-4">Are you sure you want to delete <strong>{deleteTarget.full_name}</strong>? This action cannot be undone.</p>
            {deleteError && <p className="text-sm text-red-600 mb-3">{deleteError}</p>}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50" data-testid="confirm-delete-contact-btn">{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;